"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";

import "../../marken/marken.css";

import { TopSectionMarken } from "../../marken/sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";

import höchstenPreis from "@/assets/auto-verkaufen/Frau-verkauft-ihr-auto.webp";
import BannerImage1 from "@/assets/auto-verkaufen/AutoeinfachlosBannerBig1.avif";
import BannerImage2 from "@/assets/auto-verkaufen/AutoeinfachlosBannerBig2.avif";
import BannerImage3 from "@/assets/auto-verkaufen/AutoeinfachlosBannerBig3.avif";
import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";

import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";
import { fragenAutoauktionen } from "./data";

// Ratgeber SERVERSEITIG rendern (dynamic OHNE die SSR-Abschaltung, Playbook 2.6):
// die sechs "Mehr zum Thema"-Kacheln verlinken u. a. /auto-verkaufen.
const Ratgeber = dynamic(() =>
  import("@/components/Ratgeber/Ratgeber").then((m) => m.default)
);

// Only needed when the user reaches step 12; avoid loading auth-related JS/CSS on initial paint.
const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function Autoauktionen() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionMarken
        title="Autoauktionen – So klappt der Verkauf"
        link="/guides/autoauktionen"
        linkname="Autoauktionen"
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*{" "}
            <br className="desktop" />
            Hol dir deine kostenlose und sofortige Bewertung.
          </>
        }
        removePadding={true}
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Die Autoauktion: Eine Option auf Schnäppchen"
              leftText={
                <>
                  <p>
                    Autoauktionen faszinieren viele: Der virtuelle oder reale
                    Hammer fällt, Spannung liegt in der Luft, und oft wechseln
                    Fahrzeuge blitzschnell den Besitzer. Für Verkäufer können
                    Auktionen eine Möglichkeit sein, ein Auto zügig zu
                    veräußern. Doch der Weg dorthin ist nicht immer einfach –
                    vor allem für Privatpersonen. In diesem Ratgeber erfährst
                    du, welche Arten von Autoauktionen es gibt, wie du dein Auto
                    dafür vorbereitest, welche Chancen und Risiken bestehen und
                    für wen diese Verkaufsform geeignet ist.
                  </p>
                </>
              }
              rightFirstTitle="Arten von Autoauktionen"
              rightFirstText={
                <>
                  <ol className="custom-ordered-list secondary">
                    <li>
                      <strong>Klassische Vor-Ort-Auktionen</strong>
                      <br />
                      Hier treffen sich Händler, Sammler und Bieter in
                      Auktionshallen. Fahrzeuge werden vorgeführt, es gibt oft
                      eine Besichtigungszeit vor der Auktion. Gebote werden
                      mündlich oder durch Handzeichen abgegeben. Solche
                      Auktionen sind in Österreich selten und meist auf
                      spezielle Fahrzeuge (Oldtimer, Behördenfahrzeuge)
                      spezialisiert.
                    </li>
                    <li>
                      <strong>Online-Auktionen</strong>
                      <br />
                      Die meisten Autoauktionen finden inzwischen online statt.
                      Plattformen wie BCA, Autorola oder Manheim versteigern
                      Fahrzeuge täglich. Bieter registrieren sich, hinterlegen
                      eine Sicherheitsleistung und können live oder zeitversetzt
                      bieten. In der Regel richtet sich das Angebot an Händler,
                      nicht an Privatpersonen.
                    </li>
                    <li>
                      <strong>Hybrid-Auktionen</strong>
                      <br />
                      Eine Mischung aus Vor-Ort- und Online-Auktion: Fahrzeuge
                      stehen in einer Halle, man kann sie besichtigen, die
                      eigentlichen Gebote erfolgen aber online oder telefonisch.
                      Diese Form gewinnt an Beliebtheit, weil sie flexible
                      Teilnahme ermöglicht.
                    </li>
                    <li>
                      <strong>Zoll- oder Behördenauktionen</strong>
                      <br />
                      Finanzämter und Zoll versteigern beschlagnahmte oder
                      ausgemusterte Fahrzeuge. Hier können auch Privatpersonen
                      teilnehmen, allerdings ist die Fahrzeugauswahl bunt
                      gemischt und technische Daten sind oft spärlich.
                    </li>
                  </ol>
                </>
              }
              rightSecondTitle="Der Ablauf einer Autoauktion"
              rightSecondText={
                <>
                  <ul className="custom-unordered-list secondary">
                    <li>
                      <strong>Vorbereitung des Autos:</strong> Optische
                      Aufbereitung, Dokumentation aller Mängel und vorhandenen
                      Papiere.
                    </li>
                    <li>
                      <strong>
                        Bewertung und Festlegung des Mindestpreises:
                      </strong>{" "}
                      Der Auktionsanbieter schätzt den Wert; du legst ein
                      Mindestgebot fest (Reservepreis).
                    </li>
                    <li>
                      <strong>Auktionsaufnahme:</strong> Das Fahrzeug wird
                      fotografiert, beschrieben und im Auktionskatalog gelistet.
                    </li>
                    <li>
                      <strong>Auktion:</strong> Dauer je nach Plattform von
                      wenigen Minuten bis Tagen. Interessenten bieten in
                      steigenden Schritten.
                    </li>
                    <li>
                      <strong>Zuschlag:</strong> Hat das Fahrzeug das
                      Mindestgebot erreicht, erhält der Höchstbietende den
                      Zuschlag. Ansonsten bleibt das Auto unverkauft.
                    </li>
                    <li>
                      <strong>Abwicklung:</strong> Der Käufer zahlt den
                      Auktionspreis plus Gebühren, der Verkäufer erhält den
                      Erlös minus Provision. Übergabe und Zahlungsabwicklung
                      werden vom Auktionshaus geregelt.
                    </li>
                  </ul>
                </>
              }
            />

            <div className="Banner-sections">
              <BildTextInfo
                image={BannerImage1}
                revert
                h2
                isBig
                fontSize="36px"
                title="Chancen einer Auktion"
                alt="Chancen einer Autoauktion"
                text={
                  <>
                    <ul className="custom-unordered-list">
                      <li>
                        <strong>Schneller Verkauf:</strong> Bei hoher Nachfrage
                        kann der Preis steigen.
                      </li>
                      <li>
                        <strong>Transparente Abwicklung:</strong> Der
                        Auktionsanbieter übernimmt Formalitäten.
                      </li>
                      <li>
                        <strong>Große Reichweite:</strong> Viele Händler nehmen
                        an Auktionen teil, was zu mehr Geboten führen kann.
                      </li>
                      <li>
                        <strong>Für spezielle Fahrzeuge:</strong> Oldtimer, rare
                        Sportwagen oder Behördenfahrzeuge erzielen in
                        spezialisierten Auktionen Höchstpreise.
                      </li>
                    </ul>
                  </>
                }
              />

              <BildTextInfo
                image={BannerImage2}
                h2
                isBig
                fontSize="36px"
                title="Risiken und Nachteile"
                alt="Risiken und Nachteile bei Autoauktionen"
                text={
                  <>
                    <ul className="custom-unordered-list">
                      <li>
                        <strong>Bindende Gebote:</strong> Als Verkäufer bist du
                        verpflichtet, das Fahrzeug zu verkaufen, sobald das
                        Mindestgebot erreicht ist – auch wenn du dir am Ende
                        mehr gewünscht hättest.
                      </li>
                      <li>
                        <strong>Gebühren:</strong> Auktionshäuser verlangen
                        Provisionen für Verkäufer und Käufer, teils Prozent vom
                        Verkaufspreis.
                      </li>
                      <li>
                        <strong>Keine Probefahrt:</strong> Bieter kaufen die
                        Katze im Sack. Sie bieten entsprechend vorsichtig, was
                        zu niedrigeren Geboten führen kann.
                      </li>
                      <li>
                        <strong>Beschränkter Zugang für Privatpersonen:</strong>{" "}
                        Viele Auktionen sind Händlern vorbehalten;
                        Privatverkäufer müssen oft einen Händler als Mittler
                        nutzen.
                      </li>
                      <li>
                        <strong>Transportkosten:</strong> Fahrzeuge müssen zum
                        Auktionshaus transportiert werden.
                      </li>
                    </ul>
                  </>
                }
              />

              <BildTextInfo
                image={BannerImage3}
                revert
                h2
                isBig
                fontSize="36px"
                title="Tipps für den erfolgreichen Verkauf über Auktion"
                alt="Tipps für den erfolgreichen Verkauf über Auktion"
                text={
                  <>
                    <ul className="custom-unordered-list">
                      <li>
                        <strong>Professionelle Fotos:</strong> Hochauflösende
                        Bilder von allen Seiten, Innenraum und Motor erhöhen die
                        Attraktivität.
                      </li>
                      <li>
                        <strong>Datenqualität:</strong> Detaillierte
                        Beschreibung von Ausstattung, Laufleistung,
                        Servicehistorie und Mängeln schafft Vertrauen.
                      </li>
                      <li>
                        <strong>Realistischen Mindestpreis wählen:</strong> Zu
                        hohe Mindestpreise schrecken Bieter ab. Ein
                        realistischer Preis erhöht die Chance auf einen
                        erfolgreichen Verkauf.
                      </li>
                      <li>
                        <strong>Timing beachten:</strong> Lass die Auktion
                        möglichst lange laufen (mindestens fünf Werktage) und
                        plane ein Wochenende ein, denn dann haben viele Bieter
                        Zeit.
                      </li>
                      <li>
                        <strong>Nur einmal auktionieren:</strong> Mehrfaches
                        Wiedereinstellen eines Fahrzeugs führt oft zu
                        schlechteren Ergebnissen. Nutze im Zweifelsfall mehrere
                        Auktionsplattformen parallel.
                      </li>
                    </ul>

                    <h3>Für wen lohnt sich eine Autoauktion?</h3>
                    <ul className="custom-unordered-list">
                      <li>
                        <strong>Händler und Firmen:</strong> Sie verkaufen
                        regelmäßig große Mengen und profitieren vom etablierten
                        Netzwerk.
                      </li>
                      <li>
                        <strong>Seltene Fahrzeuge:</strong> Oldtimer, Youngtimer
                        oder Spezialfahrzeuge finden hier einen speziellen
                        Käuferkreis.
                      </li>
                      <li>
                        <strong>Besitzer mit Zeitdruck:</strong> Wer das Auto
                        schnell verkaufen muss, kann mit einem Auktionserlös
                        rechnen, statt wochenlang auf private Käufer zu warten.
                      </li>
                    </ul>
                    <p>
                      Für die meisten Privatverkäufer eignet sich jedoch der
                      Verkauf über Auktionsportale nicht, weil Aufwand und
                      Risiko hoch sind. Stattdessen bieten Plattformen wie
                      Autoeinfachlos einen komfortablen Weg: Nach Eingabe
                      weniger Daten erhältst du verbindliche Angebote von
                      geprüften Händlern, musst keine Gebühren zahlen und
                      verkaufst dein Auto schnell und sicher.
                    </p>
                  </>
                }
              />
            </div>

            <HäufigeFragen
              title="Häufig gestellte Fragen"
              fragen={fragenAutoauktionen}
              image={FAQImage}
            />

            <div style={{ paddingTop: "120px" }}>
              <Ratgeber
                title="Mehr zum Thema Autoauktion"
                kaputtesAuto
                unfallAuto
                autoExport
                autoBewerten
                autoVerkaufen
                listenpreisBerechnen
              />
            </div>

            <div className="Banner-sections-marken">
              <BildTextInfo
                image={höchstenPreis}
                h2
                fontSize="36px"
                title="Fazit zum Thema Autoauktion"
                alt="Frau sitzt in einem Auto und nutzt ihr Smartphone, um mit Autoeinfachlos ihr Auto zu verkaufen."
                text={
                  <>
                    <p>
                      Autoauktionen können eine spannende und schnelle Option
                      sein, insbesondere für Händler oder Besitzer spezieller
                      Fahrzeuge. Für den klassischen Privatverkäufer überwiegen
                      jedoch Aufwand und Risiko. Wer keine Lust auf Auktionen
                      hat und dennoch schnell und sicher verkaufen möchte, nutzt
                      Autoeinfachlos: Binnen weniger Stunden erhältst du seriöse
                      Angebote und verkaufst zum besten Preis – ohne Risiko und
                      versteckte Kosten.
                    </p>
                  </>
                }
              />
            </div>
          </div>
          <Banner
            link="/auto-verkaufen"
            title="Jetzt Auto kostenlos bewerten & sicher verkaufen"
          />
        </div>
      )}
    </div>
  );
}

export default Autoauktionen;
